import React, { useState, useEffect, useContext, useCallback } from "react";
import { UserContext } from "../../../contexts/user.context";
import { Get, Post, handleError } from "../../../utils/fetch.util";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import LoadingData from "../../../components/loadingData/loadingData.component";
import {
  FiUser,
  FiTruck,
  FiX,
  FiCheck,
  FiXCircle,
  FiClock,
  FiLogIn,
  FiLogOut,
} from "react-icons/fi";
import Select from "react-select";
import { debounce } from "lodash";
import Image from "../../../components/Image/Image.component";

interface VisitorRequest {
  id: number;
  name: string;
  reason: string;
  nationalId: string;
  phone: string;
  qrcode: string;
  qrcode_url: string;
  startDate: string;
  endDate: string;
  allowVehicle: boolean;
  current_status: "pending" | "approved" | "rejected";
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse {
  status: string;
  count: number;
  data: VisitorRequest[];
}

export default function VisitorEntryExit() {
  const { t } = useTranslation();
  const { getValidToken, userData } = useContext(UserContext);

  // State management
  const [visitorRequests, setVisitorRequests] = useState<VisitorRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString());
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const [endDate, setEndDate] = useState(tomorrow.toISOString());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [activeTab, setActiveTab] = useState<"visitor" | "vehicle">("visitor");

  // Entry/Exit modal state
  const [showEntryExitModal, setShowEntryExitModal] = useState(false);
  const [entryExitLoading, setEntryExitLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<VisitorRequest | null>(null);
  const [entryExitStatus, setEntryExitStatus] = useState<"entry" | "exit">("entry");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [modalSiteId, setModalSiteId] = useState<{ value: number; label: string } | null>(null);

  const sites = userData[0]?.user_sites_relationShip?.map((site: any) => site?.site) || [];
  const sitesOptions = sites.map((site: any) => ({
    value: site.site_id,
    label: site.site_name,
  }));

  const [filterSiteId, setFilterSiteId] = useState<{ value: number; label: string } | null>(null);

  // Fetch visitor requests
  const fetchVisitorRequests = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        pageItemsCount: pageSize.toString(),
        vehicle_only: activeTab === "vehicle" ? "true" : "false",
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(filterSiteId?.value && { site_id: filterSiteId.value.toString() }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        // Only show approved requests
        approval_status: JSON.stringify(["approved"]),
      } as any);

      const response = await Get(
        `visitor-requests/get-all-visitor-requests?${queryParams}`,
        getValidToken()
      );

      if (response.error || !response.data) {
        handleError(response);
        return;
      }

      const apiResponse: ApiResponse = response;
      setVisitorRequests(apiResponse.data);
      setTotalCount(apiResponse.count);
    } catch (error) {
      console.error("Error fetching visitor requests:", error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce search term changes
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      setDebouncedSearchTerm(term);
    }, 500),
    []
  );

  // Update debounced search term when search term changes
  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterSiteId, startDate, endDate, activeTab]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchVisitorRequests();
  }, [currentPage, debouncedSearchTerm, filterSiteId, startDate, endDate, activeTab]);

  // Open entry/exit modal
  const openEntryExitModal = (request: VisitorRequest, status: "entry" | "exit") => {
    setSelectedRequest(request);
    setEntryExitStatus(status);
    setVehicleNumber("");
    setModalSiteId(null);
    setShowEntryExitModal(true);
  };

  // Handle entry/exit submission
  const handleEntryExit = async () => {
    if (!selectedRequest || !modalSiteId) return;

    setEntryExitLoading(true);
    try {
      const response = await Post(
        "visitor-requests/create-visitor-entry-log",
        {
          visitor_request_id: selectedRequest.id,
          status: entryExitStatus,
          site_id: modalSiteId.value,
          vehicle_number: vehicleNumber.trim() || undefined,
        },
        getValidToken()
      );

      if (response.error) {
        handleError(response);
        return;
      }

      // Close modal and refresh data
      setShowEntryExitModal(false);
      fetchVisitorRequests();
    } catch (error) {
      console.error("Error creating entry/exit log:", error);
    } finally {
      setEntryExitLoading(false);
    }
  };

  // Status badge component
  const StatusBadge = ({ status }: { status: string }) => {
    const getStatusStyles = () => {
      switch (status) {
        case "approved":
          return "bg-green-100 text-green-800";
        case "rejected":
          return "bg-red-100 text-red-800";
        case "pending":
        default:
          return "bg-yellow-100 text-yellow-800";
      }
    };

    const getStatusIcon = () => {
      switch (status) {
        case "approved":
          return <FiCheck className="w-4 h-4 mr-1" />;
        case "rejected":
          return <FiXCircle className="w-4 h-4 mr-1" />;
        case "pending":
        default:
          return <FiClock className="w-4 h-4 mr-1" />;
      }
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyles()}`}
      >
        {getStatusIcon()}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Visitor request card component
  const VisitorRequestCard = ({ request }: { request: VisitorRequest }) => {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4 border border-gray-100">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
              <span className="text-lg font-semibold text-gray-600">{request.name.charAt(0)}</span>
            </div>
            <div>
              <h3 className="text-lg font-medium text-blue-600">
                {request.name}
              </h3>
              <p className="text-sm text-gray-500">Visitor</p>
            </div>
          </div>
          <StatusBadge status={request.current_status} />
        </div>

        <div>
          <Image
            src={request?.qrcode_url}
            alt={request.name}
            className="w-[75%] mx-auto"
          />
        </div>

        <div className="mt-3 text-sm text-gray-600 space-y-2">
          <p>
            <span className="font-medium">Created at:</span> {format(new Date(request.createdAt), "MM/dd/yyyy")}
          </p>
          <p>
            <span className="font-medium">Approved at:</span> {format(new Date(request.updatedAt), "MM/dd/yyyy")}
          </p>
          <p>
            <span className="font-medium">National ID:</span> {request.nationalId}
          </p>
          <p>
            <span className="font-medium">{format(new Date(request.startDate), "MM/dd/yyyy")} - {format(new Date(request.endDate), "MM/dd/yyyy")}</span>
          </p>
          {request.reason && (
            <p>
              <span className="font-medium">Approval Reason:</span> {request.reason}
            </p>
          )}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <button
            onClick={() => openEntryExitModal(request, "entry")}
            className="flex items-center justify-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
          >
            <FiLogIn className="mr-2" /> Entry
          </button>
          <button
            onClick={() => openEntryExitModal(request, "exit")}
            className="flex items-center justify-center px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
          >
            <FiLogOut className="mr-2" /> Exit
          </button>
        </div>
      </div>
    );
  };

  // Pagination component
  const Pagination = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxPagesToShow = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex justify-center mt-6 mb-4">
        <nav className="flex items-center space-x-1">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-md bg-gray-100 text-gray-700 disabled:opacity-50"
          >
            Previous
          </button>

          {startPage > 1 && (
            <>
              <button
                onClick={() => setCurrentPage(1)}
                className={`px-3 py-1 rounded-md ${
                  currentPage === 1
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                1
              </button>
              {startPage > 2 && (
                <span className="px-2 py-1 text-gray-500">...</span>
              )}
            </>
          )}

          {pageNumbers.map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1 rounded-md ${
                currentPage === page
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {page}
            </button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <span className="px-2 py-1 text-gray-500">...</span>
              )}
              <button
                onClick={() => setCurrentPage(totalPages)}
                className={`px-3 py-1 rounded-md ${
                  currentPage === totalPages
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded-md bg-gray-100 text-gray-700 disabled:opacity-50"
          >
            Next
          </button>
        </nav>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">Visitor Entry/Exit</h1>

      {/* Tabs */}
      <div className="flex mb-4 border-b">
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === "visitor"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("visitor")}
        >
          <FiUser className="inline mr-2" />
          Visitor
        </button>
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === "vehicle"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("vehicle")}
        >
          <FiTruck className="inline mr-2" />
          Vehicle
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
            Search
          </label>
          <input
            type="text"
            id="search"
            placeholder="Search by visitor name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="site" className="block text-sm font-medium text-gray-700 mb-1">
            Select a Site
          </label>
          <Select
            id="site"
            options={sitesOptions}
            value={filterSiteId}
            onChange={(option) => setFilterSiteId(option)}
            isClearable
            placeholder="Select a site"
            className="basic-single"
            classNamePrefix="select"
          />
        </div>
{/* 
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div> */}
{/* 
        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
            End Date
          </label>
          <input
            type="date"
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div> */}
      </div>

      {/* Content */}
      {loading ? (
        <LoadingData />
      ) : (
        <>
          {visitorRequests?.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No approved visitor requests found</p>
            </div>
          ) : (
            <div className="mb-6 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {visitorRequests?.map((request) => (
                <VisitorRequestCard key={request.id} request={request} />
              ))}
            </div>
          )}
          <Pagination />
        </>
      )}

      {/* Entry/Exit Modal */}
      {showEntryExitModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {entryExitStatus === "entry" ? "Visitor Entry" : "Visitor Exit"}
              </h2>
              <button
                onClick={() => setShowEntryExitModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-6">
              <div className="bg-gray-50 p-3 rounded-md mb-4">
                <p className="text-sm text-gray-700">
                  <strong>Visitor:</strong> {selectedRequest.name}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>National ID:</strong> {selectedRequest.nationalId}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Visit Period:</strong> {format(new Date(selectedRequest.startDate), "MMM dd, yyyy")} - {format(new Date(selectedRequest.endDate), "MMM dd, yyyy")}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Reason:</strong> {selectedRequest.reason}
                </p>
              </div>

              {/* Site Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Site *
                </label>
                <Select
                  options={sitesOptions}
                  value={modalSiteId}
                  onChange={(option) => setModalSiteId(option)}
                  className="basic-single"
                  classNamePrefix="select"
                  placeholder="Select site"
                  isDisabled={entryExitLoading}
                  required
                />
              </div>

              {/* Vehicle Number */}
              {selectedRequest.allowVehicle && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vehicle Number {entryExitStatus === "entry" && "*"}
                  </label>
                  <input
                    type="text"
                    value={vehicleNumber}
                    onChange={(e) => setVehicleNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter vehicle number"
                    disabled={entryExitLoading}
                    required={entryExitStatus === "entry"}
                  />
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setShowEntryExitModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                disabled={entryExitLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleEntryExit}
                disabled={
                  entryExitLoading ||
                  !modalSiteId ||
                  (selectedRequest.allowVehicle && entryExitStatus === "entry" && !vehicleNumber.trim())
                }
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {entryExitLoading
                  ? "Processing..."
                  : entryExitStatus === "entry"
                  ? "Record Entry"
                  : "Record Exit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
